import React, { useEffect, useState } from 'react';
import {
    Text,
    Spinner,
    SpinnerSize,
    MessageBar,
    MessageBarType,
    Dropdown,
    IDropdownOption,
    useTheme,
    Pivot,
    PivotItem,
} from '@fluentui/react';
import { CloudData, StageType } from '../models/CloudStatusModels';
import { CloudStatusSummary } from '../models/CloudStatusApiModels';
import {
    fetchCloudList,
    fetchCloudStatusByName,
} from '../services/CloudStatusService';
import { useIsMobile } from '../../Common/hooks/useIsMobile';
import { HubHeader } from '../../Common/components/HubHeader';
import { ServiceStatusList } from './ServiceStatusList';
import { ComponentDetails } from './ComponentDetails';
import { CurrentTab } from './tabs/CurrentTab';
import { PipelinesTab } from './tabs/PipelinesTab';
import { FeaturesTab } from './tabs/FeaturesTab';
import { getStatusColor } from '../utils/StatusHelpers';
import '../styles/CommonStyles.css';
import { HeartbeatUptime } from './HeartbeatUptime';

export const CloudStatus: React.FC = () => {
    const theme = useTheme();
    const isMobile = useIsMobile();
    const [cloudList, setCloudList] = useState<CloudStatusSummary[]>([]);
    const [selectedCloudName, setSelectedCloudName] = useState<string | null>(
        null
    );
    const [selectedCloudData, setSelectedCloudData] =
        useState<CloudData | null>(null);
    const [loadingList, setLoadingList] = useState<boolean>(true);
    const [loadingDetails, setLoadingDetails] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedStage, setSelectedStage] = useState<StageType>('Overall');
    const [selectedTab, setSelectedTab] = useState<string>('current');
    const [selectedComponentId, setSelectedComponentId] = useState<
        string | null
    >(null);

    // Load cloud list on mount
    useEffect(() => {
        const loadCloudList = async () => {
            try {
                setLoadingList(true);
                const list = await fetchCloudList();
                setCloudList(list);

                // Auto-select first cloud if available
                if (list.length > 0) {
                    setSelectedCloudName(list[0].cloudName);
                }

                setError(null);
            } catch (err) {
                setError('Failed to load cloud list');
                console.error('Error loading cloud list:', err);
            } finally {
                setLoadingList(false);
            }
        };

        loadCloudList();
    }, []);

    // Load selected cloud details when selection changes
    useEffect(() => {
        const loadCloudDetails = async () => {
            if (!selectedCloudName) {
                setSelectedCloudData(null);
                return;
            }

            try {
                setLoadingDetails(true);
                const data = await fetchCloudStatusByName(selectedCloudName);
                setSelectedCloudData(data || null);
                setError(null);
            } catch (err) {
                setError(`Failed to load details for ${selectedCloudName}`);
                console.error('Error loading cloud details:', err);
            } finally {
                setLoadingDetails(false);
            }
        };

        loadCloudDetails();
    }, [selectedCloudName]);

    const cloudOptions: IDropdownOption[] = cloudList.map((cloud) => ({
        key: cloud.cloudName,
        text: cloud.cloudName,
    }));

    const handleCloudChange = (
        event: React.FormEvent<HTMLDivElement>,
        option?: IDropdownOption
    ) => {
        if (option) {
            setSelectedCloudName(option.key as string);
        }
    };

    // Filter services based on selected stage
    const getFilteredServices = () => {
        if (!selectedCloudData) {
            return [];
        }
        if (selectedStage === 'Overall') {
            return selectedCloudData.services;
        }
        return selectedCloudData.services.filter(
            (s) => s.stage === selectedStage
        );
    };

    return (
        <div
            className="CloudStatus-root"
            style={{
                minHeight: '100vh',
                scrollbarWidth: 'none', // Firefox
                msOverflowStyle: 'none', // IE and Edge
            }}
        >
            {/* All-clouds-at-once heartbeat uptime overview — independent of the per-cloud dropdown below */}
            <HeartbeatUptime />

            {/* Unified Header and Tabs Section */}
            <div
                style={{
                    backgroundColor: theme.palette.white,
                    borderRadius: '4px',
                    boxShadow:
                        '0 3.2px 7.2px 0 rgba(0, 0, 0, .132), 0 .6px 1.8px 0 rgba(0, 0, 0, .108)',
                    overflow: 'hidden',
                    marginBottom: '16px',
                }}
            >
                {/* Header with Cloud Selection */}
                <HubHeader
                    title="Cloud Status"
                    subtitle={selectedCloudName}
                    actions={
                        !loadingList && cloudList.length > 0 ? (
                            <Dropdown
                                placeholder="Select a cloud"
                                options={cloudOptions}
                                selectedKey={selectedCloudName || undefined}
                                onChange={handleCloudChange}
                                styles={{
                                    dropdown: {
                                        width: isMobile ? '100%' : 250,
                                    },
                                    root: {
                                        width: isMobile ? '100%' : 250,
                                    },
                                }}
                            />
                        ) : undefined
                    }
                />

                {/* Loading State for initial cloud list */}
                {loadingList && (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            padding: '60px 20px',
                        }}
                    >
                        <Spinner
                            size={SpinnerSize.large}
                            label="Loading cloud list..."
                        />
                    </div>
                )}

                {/* Error Message */}
                {error && !loadingList && (
                    <div style={{ padding: '20px' }}>
                        <MessageBar
                            messageBarType={MessageBarType.error}
                            isMultiline={false}
                        >
                            {error}
                        </MessageBar>
                    </div>
                )}

                {/* No Clouds Message */}
                {!loadingList && cloudList.length === 0 && !error && (
                    <div style={{ padding: '20px' }}>
                        <MessageBar
                            messageBarType={MessageBarType.info}
                            isMultiline={false}
                        >
                            No clouds available
                        </MessageBar>
                    </div>
                )}

                {/* Tabs - Only show when cloud data is available or loading */}
                {!loadingList && selectedCloudName && (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <Pivot
                            aria-label="Cloud Status Sections"
                            selectedKey={selectedTab}
                            onLinkClick={(item) => {
                                if (item?.props.itemKey) {
                                    setSelectedTab(item.props.itemKey);
                                }
                            }}
                            styles={{
                                root: {
                                    padding: '0 20px',
                                    backgroundColor:
                                        theme.palette.neutralLighterAlt,
                                },
                            }}
                        >
                            <PivotItem
                                headerText="Current"
                                itemKey="current"
                                itemIcon="SkypeCircleCheck"
                            >
                                {loadingDetails ? (
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            padding: '60px 20px',
                                        }}
                                    >
                                        <Spinner
                                            size={SpinnerSize.large}
                                            label={`Loading ${selectedCloudName}...`}
                                        />
                                    </div>
                                ) : selectedCloudData ? (
                                    <div
                                        style={{
                                            padding: '20px',
                                            borderTop: `10px solid ${getStatusColor(
                                                selectedCloudData.overallHealth
                                            )}`,
                                        }}
                                    >
                                        <CurrentTab
                                            cloudData={selectedCloudData}
                                            selectedStage={selectedStage}
                                            onStageClick={setSelectedStage}
                                        />
                                    </div>
                                ) : null}
                            </PivotItem>

                            <PivotItem
                                headerText="Pipelines"
                                itemKey="pipelines"
                                itemIcon="Streaming"
                            >
                                {loadingDetails ? (
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            padding: '60px 20px',
                                        }}
                                    >
                                        <Spinner
                                            size={SpinnerSize.large}
                                            label={`Loading ${selectedCloudName}...`}
                                        />
                                    </div>
                                ) : selectedCloudData ? (
                                    <div style={{ padding: '20px' }}>
                                        <PipelinesTab
                                            pipelines={
                                                selectedCloudData.pipelines
                                            }
                                        />
                                    </div>
                                ) : null}
                            </PivotItem>

                            <PivotItem
                                headerText="Features"
                                itemKey="features"
                                itemIcon="CheckboxComposite"
                            >
                                {loadingDetails ? (
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            padding: '60px 20px',
                                        }}
                                    >
                                        <Spinner
                                            size={SpinnerSize.large}
                                            label={`Loading ${selectedCloudName}...`}
                                        />
                                    </div>
                                ) : selectedCloudData ? (
                                    <div style={{ padding: '20px' }}>
                                        <FeaturesTab
                                            features={
                                                selectedCloudData.features
                                            }
                                        />
                                    </div>
                                ) : null}
                            </PivotItem>
                        </Pivot>
                    </div>
                )}
            </div>

            {/* Service Status and Component Details - Side by side on desktop, stacked on mobile */}
            {!loadingList && !loadingDetails && selectedCloudData && (
                <div
                    className="CloudCard-root"
                    style={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: '16px',
                        alignItems: 'flex-start',
                    }}
                >
                    {/* Services List */}
                    <div
                        style={{
                            flex: isMobile ? '1 1 auto' : '0 0 calc(40% - 8px)',
                            minHeight: '500px',
                            width: isMobile ? '100%' : undefined,
                        }}
                    >
                        <ServiceStatusList
                            services={getFilteredServices()}
                            selectedStage={selectedStage}
                            onComponentSelect={setSelectedComponentId}
                            selectedComponentId={selectedComponentId}
                        />
                    </div>

                    {/* Component Details */}
                    <div
                        style={{
                            flex: isMobile ? '1 1 auto' : '0 0 calc(60% - 8px)',
                            minHeight: '500px',
                            width: isMobile ? '100%' : undefined,
                        }}
                    >
                        <ComponentDetails
                            selectedComponentId={selectedComponentId}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
